import {
  Autocomplete,
  Direction,
  FormField,
  PopUp,
  PopUpProps
} from '@app/form_components';
import { useAsync, useServices } from '@app/hooks';
import styles from '@app/styles/components/modal.module.scss';
import {
  AudioType,
  DesignLanguage,
  LanguageCode,
  Option
} from '@app/types';
import { Button, useRect } from '@platform/ui-library';
import { IconType } from '@platform/ui-library/build/Icon';
import {
  FC,
  useCallback,
  useEffect,
  useState
} from 'react';

export const languageTypeLabels: Record<AudioType | 'language', string> = {
  language: 'Language',
  [AudioType.tts]: 'TTS Voice',
  [AudioType.ttp]: 'Voice Talent'
};

const designLanguageSeed: DesignLanguage = {
  language: LanguageCode.spanishUs,
  voice: ''
};

type LanguageFormProps = Partial<PopUpProps> & {
  audioType?: AudioType;
  deletable?: boolean;
  labels?: Record<AudioType | 'language', string>;
  language?: DesignLanguage;
  onSave: (language: DesignLanguage, action: 'update' | 'delete') => void;
};

export const LanguageForm: FC<LanguageFormProps> = ({
  children,
  deletable = true,
  labels = null,
  language: initialLanguage,
  onSave
}) => {
  const { globalModalsService, workflowService } = useServices();
  const [updatedLanguage, setUpdatedLanguage] = useState({ ...(initialLanguage ?? designLanguageSeed) });

  const { language: languageCode } = updatedLanguage || {};

  const [setRef, refRect, updateRect] = useRect();
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    updateRect();
  }, [isOpen]);

  const [languageOptions] = useAsync(() => workflowService.getLanguages(), []);
  const [vtOptions] = useAsync(() =>
    workflowService.getVoices(AudioType.ttp, languageCode)
  , [languageCode]);
  const [ttsOptions] = useAsync(() =>
    workflowService.getVoices(AudioType.tts, languageCode)
  , [languageCode]);

  const filterOptions = async (
    options: Option<string>[],
    searchValue: string,
    selectedValue: string | undefined
  ) => {
    const isSearchSelected = options.some(({ label, value }) =>
      value === selectedValue
      && label === searchValue
    );
    if (isSearchSelected) {
      return options?.slice(0, 20);
    }

    const searchValueLower = searchValue.toLowerCase().trim();
    return options.filter(({ label, value }) => {
      return (
        label.toLowerCase().includes(searchValueLower)
        || value.toLowerCase().includes(searchValueLower)
      );
    });
  };

  const autocompleteProps = useCallback((propsType: AudioType | 'language') => {
    const key = ({
      [AudioType.tts]: 'ttsFallBackVoice',
      [AudioType.ttp]: 'voice',
      language: 'language'
    } as const)[propsType];

    return {
      icon: 'search' as IconType,
      initSearchValue: labels?.[propsType],
      labels: {
        title: '',
        notFound: `No ${languageTypeLabels[propsType]?.toLowerCase()} was found`
      },
      minSearchLength: 0,
      onValueChange: (value: string) => {
        const seed = (propsType === 'language') ? designLanguageSeed : updatedLanguage;
        setUpdatedLanguage({ ...seed, [key]: value });
      },
      value: updatedLanguage?.[key] ?? null
    };
  }, [updatedLanguage, languageCode, setUpdatedLanguage]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatedLanguage.language) {
      setErrorMessage('Please select a language');
      return;
    }
    if (!updatedLanguage.voice && !updatedLanguage.ttsFallBackVoice) {
      setErrorMessage('Please select at least a TTP or a TTS voice');
      return;
    }
    onSave(updatedLanguage, 'update');
    setIsOpen(false);
  };

  const onRemove = async () => {
    const confirm = await globalModalsService.openConfirmationModal({
      children: `Are you sure you want to delete "${labels?.language}"?`,
      labels: {
        header: 'Delete confirmation',
        cancelButton: 'Cancel',
        continueButton: 'Delete'
      }
    });
    if (!confirm) {
      return;
    }
    onSave(updatedLanguage!, 'delete');
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        ref={setRef as any}
      >
        {children}
      </button>
      {(isOpen) && (
        <PopUp
          className={styles.popUp}
          direction={Direction.right}
          handleClose={() => setIsOpen(false)}
          key={String(isOpen)}
          refRect={refRect}
          useBackdrop
        >
          <form
            onSubmit={onSubmit}
            onInput={() => setErrorMessage('')}
          >
            <div className={styles.form}>
              <FormField label={languageTypeLabels.language}>
                <Autocomplete
                  {...autocompleteProps('language')}
                  autoFocus
                  disabled={!!initialLanguage?.language}
                  getOptions={(searchValue: string) => filterOptions(languageOptions ?? [], searchValue, languageCode)}
                  placeholder="Select a language"
                />
              </FormField>
              <FormField
                label={languageTypeLabels[AudioType.ttp]}
                tooltip='Selecting "<None>" will result in a fully TTS language.'
              >
                <Autocomplete
                  {...autocompleteProps(AudioType.ttp)}
                  disabled={!languageCode}
                  emptyLabel='<None>'
                  getOptions={(searchValue: string) => filterOptions(vtOptions ?? [], searchValue, updatedLanguage.voice)}
                  placeholder={(languageCode) ? 'Select a voice talent' : undefined}
                />
              </FormField>
              <FormField label={languageTypeLabels[AudioType.tts]}>
                <Autocomplete
                  {...autocompleteProps(AudioType.tts)}
                  disabled={!languageCode}
                  emptyLabel='<None>'
                  getOptions={(searchValue: string) => filterOptions(ttsOptions ?? [], searchValue, updatedLanguage.ttsFallBackVoice)}
                  placeholder={(languageCode) ? 'Select a TTS voice' : undefined}
                />
              </FormField>
              {(!errorMessage) ? null : (
                <div className={styles.errorMessage}>
                  <i className="fe icon-alert-octagon" />
                  {errorMessage}
                </div>
              )}
              <div className={styles.actions}>
                {(!deletable) ? null : (
                  <Button
                    type="button"
                    theme="outlineHighlight"
                    onClick={onRemove}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  type="button"
                  theme="outline"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button disabled={!languageCode}>Save</Button>
              </div>
            </div>
          </form>
        </PopUp>
      )}
    </>
  );
};
